/**
 * React Query hooks for volume file operations.
 * Supports both user-scoped and group-scoped volumes via optional groupId.
 *
 * @module hooks/useVolumeFiles
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { volumeFileService } from '@/services';
import { volumeKeys } from './useVolumes';
import { sandboxGroupKeys } from './useSandboxGroups';

/**
 * Query key factory for volume files.
 */
export const volumeFileKeys = {
  all: (volumeName: string, groupId?: string) =>
    groupId
      ? (['volumeFiles', 'group', groupId, volumeName] as const)
      : (['volumeFiles', volumeName] as const),
  directory: (volumeName: string, path: string, groupId?: string) =>
    [...volumeFileKeys.all(volumeName, groupId), 'dir', path] as const,
};

/**
 * Hook to list directory contents within a volume.
 */
export function useVolumeDirectory(volumeName: string, path: string, groupId?: string) {
  return useQuery({
    queryKey: volumeFileKeys.directory(volumeName, path, groupId),
    queryFn: () => volumeFileService.listDirectory(volumeName, path, groupId),
    enabled: !!volumeName,
  });
}

/**
 * Hook to upload a file to a volume.
 */
export function useUploadVolumeFile(volumeName: string, groupId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      path,
      file,
      overwrite,
      onUploadProgress,
      signal,
    }: {
      path: string;
      file: File;
      overwrite?: boolean;
      onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void;
      signal?: AbortSignal;
    }) =>
      volumeFileService.uploadFile(
        volumeName,
        path,
        file,
        overwrite,
        onUploadProgress,
        signal,
        groupId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: volumeFileKeys.all(volumeName, groupId) });
      if (groupId) {
        queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.volumes(groupId) });
      } else {
        queryClient.invalidateQueries({ queryKey: volumeKeys.detail(volumeName) });
      }
    },
  });
}

/**
 * Hook to delete a file or directory from a volume.
 */
export function useDeleteVolumePath(volumeName: string, groupId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ path, recursive }: { path: string; recursive?: boolean }) =>
      volumeFileService.deletePath(volumeName, path, recursive, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: volumeFileKeys.all(volumeName, groupId) });
      if (groupId) {
        queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.volumes(groupId) });
      } else {
        queryClient.invalidateQueries({ queryKey: volumeKeys.detail(volumeName) });
      }
    },
  });
}

/**
 * Hook to create a directory in a volume.
 */
export function useCreateVolumeDirectory(volumeName: string, groupId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => volumeFileService.createDirectory(volumeName, path, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: volumeFileKeys.all(volumeName, groupId) });
      if (groupId) {
        queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.volumes(groupId) });
      } else {
        queryClient.invalidateQueries({ queryKey: volumeKeys.detail(volumeName) });
      }
    },
  });
}
